import React, { useEffect, useState } from "react";
import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, TextField, Typography } from "@mui/material";
import { transitionAuditControl, unwrapPost } from "../../../../helpers/auditHub_helper";
import {
  AH_ACCENT,
  AH_ACCENT_HOVER,
  AH_INPUT_BORDER,
  AH_MUTED,
  AH_TEXT,
  AH_TRACK,
  AH_WHITE,
  auditHubPageSx,
} from "../../AuditHub/theme";

/** The note is stored as a comment, so it shares the comment body limit. */
const NOTE_MAX = 4000;

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    fontSize: "0.875rem",
    backgroundColor: AH_WHITE,
    "& fieldset": { borderColor: AH_INPUT_BORDER },
    "&:hover fieldset": { borderColor: "#cbd5e1" },
    "&.Mui-focused fieldset": { borderColor: AH_TEXT, borderWidth: "1px" },
  },
  "& .MuiInputLabel-root": { fontSize: "0.875rem", color: AH_MUTED },
  "& .MuiInputLabel-root.Mui-focused": { color: AH_TEXT },
  "& .MuiInputBase-input": { fontSize: "0.875rem", color: AH_TEXT },
  "& .MuiFormHelperText-root": { mx: 0.25, fontSize: 11.5 },
};

const closeXSx = {
  p: 0,
  m: 0,
  border: "none",
  background: "transparent",
  color: AH_WHITE,
  fontSize: 34,
  fontWeight: 900,
  lineHeight: 0.85,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  flexShrink: 0,
  WebkitTextStroke: `0.6px ${AH_WHITE}`,
  "&:disabled": { cursor: "not-allowed", opacity: 0.6 },
};

const buttonBaseSx = {
  textTransform: "none",
  fontSize: "0.875rem",
  borderRadius: "8px",
  px: 2.5,
  py: 0.85,
  flexShrink: 0,
  boxShadow: "none",
};

const greyButtonSx = {
  ...buttonBaseSx,
  fontWeight: 600,
  color: AH_WHITE,
  backgroundColor: "#6c757d",
  border: "1px solid #6c757d",
  "&:hover": { backgroundColor: "#5a6268", borderColor: "#5a6268", boxShadow: "none" },
  "&.Mui-disabled": { backgroundColor: AH_INPUT_BORDER, color: "#94a3b8", borderColor: AH_INPUT_BORDER },
};

const primaryButtonSx = {
  ...buttonBaseSx,
  fontWeight: 700,
  color: AH_WHITE,
  backgroundColor: AH_ACCENT,
  "&:hover": { backgroundColor: AH_ACCENT_HOVER, boxShadow: "none" },
  "&.Mui-disabled": { backgroundColor: "#fed7aa", color: AH_WHITE },
};

const RequestInfoDialog = ({ open, onClose, onDone, auditId, auditControlId, controlCode }) => {
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      setNote("");
      setSaving(false);
      setError(null);
    }
  }, [open]);

  const trimmed = note.trim();
  const canSubmit = Boolean(trimmed) && !saving;

  const close = () => {
    if (saving) return;
    onClose();
  };

  const submit = async () => {
    if (!canSubmit) return;
    setSaving(true);
    setError(null);
    try {
      unwrapPost(await transitionAuditControl(auditId, auditControlId, { toStatus: "NEEDS_MORE_INFO", note: trimmed }));
      setSaving(false);
      onDone();
    } catch (err) {
      setError(err?.message || "Unable to request more information.");
      setSaving(false);
    }
  };

  const fieldId = (name) => `request-info-${name}-${auditControlId}`;

  return (
    <Dialog
      open={open}
      onClose={close}
      fullWidth
      maxWidth="sm"
      aria-labelledby={fieldId("title")}
      PaperProps={{
        component: "form",
        noValidate: true,
        onSubmit: (event) => {
          event.preventDefault();
          submit();
        },
        sx: {
          ...auditHubPageSx,
          borderRadius: "12px",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          boxShadow: "none",
          border: `1px solid ${AH_INPUT_BORDER}`,
        },
      }}
    >
      <Box
        sx={{
          flexShrink: 0,
          px: { xs: 2, sm: 3 },
          py: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          backgroundColor: AH_ACCENT,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            id={fieldId("title")}
            sx={{ fontWeight: 700, fontSize: { xs: "1.125rem", sm: "1.25rem" }, color: AH_WHITE, letterSpacing: "-0.02em", lineHeight: 1.2 }}
          >
            Request More Info
          </Typography>
          <Typography sx={{ mt: 0.75, color: "rgba(255,255,255,0.9)", fontSize: "0.875rem" }}>
            Explain what is needed for {controlCode || "this control"}. The note is posted to its activity.
          </Typography>
        </Box>
        <Box component="button" type="button" aria-label="Close" onClick={close} disabled={saving} sx={closeXSx}>
          <span aria-hidden="true">×</span>
        </Box>
      </Box>

      <DialogContent sx={{ p: { xs: 1.5, sm: 2.5 }, backgroundColor: AH_TRACK }}>
        <Box sx={{ backgroundColor: AH_WHITE, border: `1px solid ${AH_INPUT_BORDER}`, borderRadius: "12px", p: 2 }}>
          <TextField
            id={fieldId("note")}
            label="Note"
            required
            autoFocus
            fullWidth
            multiline
            minRows={4}
            maxRows={10}
            size="small"
            value={note}
            disabled={saving}
            onChange={(event) => {
              setNote(event.target.value);
              if (error) setError(null);
            }}
            helperText={`${note.length}/${NOTE_MAX}`}
            inputProps={{ maxLength: NOTE_MAX }}
            sx={fieldSx}
          />
        </Box>
      </DialogContent>

      <DialogActions
        disableSpacing
        sx={{
          px: 2.5,
          py: 1.5,
          gap: 1.5,
          flexWrap: { xs: "wrap", sm: "nowrap" },
          borderTop: `1px solid ${AH_INPUT_BORDER}`,
          bgcolor: AH_WHITE,
          justifyContent: "space-between",
        }}
      >
        <Button type="button" variant="contained" disableElevation onClick={close} disabled={saving} sx={greyButtonSx}>
          Cancel
        </Button>
        {error ? (
          <Typography
            role="alert"
            sx={{
              flex: 1,
              minWidth: 0,
              order: { xs: 3, sm: 0 },
              flexBasis: { xs: "100%", sm: "auto" },
              fontSize: "0.8125rem",
              lineHeight: 1.45,
              color: "#dc2626",
              textAlign: "center",
              overflowWrap: "anywhere",
            }}
          >
            {error}
          </Typography>
        ) : null}
        <Button
          type="submit"
          variant="contained"
          disableElevation
          disabled={!canSubmit}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
          sx={primaryButtonSx}
        >
          {saving ? "Sending..." : "Send Request"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RequestInfoDialog;
